// Copyright (c) StrangeBee 2024

import { Button } from 'antd';
import React, { ReactElement, useEffect, useState } from 'react';
import { DescribeFieldOutput, FilterInput } from 'thehive-sdk';
import FilterAttributes from '@Molecules/FiltersList/FiltersAttributes';
import FilterOperator from '@Molecules/FiltersList/FiltersOperator';
import FilterValues from '@Molecules/FiltersList/FiltersValues';
import { FilterInputForm } from '@Hooks/useFilters';
import { ICONS } from '@Constants/iconsApp';
import { generateId } from '@Helpers/string';

interface FiltersListFieldProps {
    describeField?: DescribeFieldOutput;
    setFilters: (callback: (filters: FilterInputForm[]) => FilterInputForm[]) => void;
    filter?: Partial<FilterInput>;
    index: number;
    vertical?: boolean;
    removeTempFilter: (index: number) => void;
    aggregationWarning?: boolean;
}

const FiltersListField = React.memo(
    ({
        describeField,
        setFilters,
        filter = {},
        index,
        vertical = false,
        removeTempFilter,
        aggregationWarning = false,
    }: FiltersListFieldProps): ReactElement => {
        const [id, setId] = useState('');
        const onFilterChange = (filterValue: Partial<FilterInput>): void => {
            setFilters((prevFilters) => {
                const newFilters = [...prevFilters];
                newFilters[index] = filterValue;
                return newFilters;
            });
        };

        const onFilterFieldChange = (filterValue: Partial<FilterInput>): void => {
            const fieldAttributes = describeField?.attributes.find((attribute) => attribute.name === filterValue.field);

            if (fieldAttributes) {
                const newFilter = {
                    ...filterValue,
                    type: fieldAttributes?.type,
                    describeAttribute: fieldAttributes,
                };

                onFilterChange(newFilter);
            }
        };

        useEffect(() => {
            const tmpId = generateId();
            setId(tmpId);
        }, [filter]);

        return (
            <div className={`complete-filter-field ${vertical ? 'vertical' : ''}`}>
                <div className="fields">
                    <FilterAttributes
                        describeField={describeField}
                        filter={filter}
                        onChange={(newValue) => {
                            onFilterFieldChange({ field: newValue });
                        }}
                        key={id}
                        aggregationWarning={aggregationWarning}
                    />

                    {filter.field && (
                        <FilterOperator
                            describeField={describeField}
                            operator={filter?.operator}
                            onChange={(newValue): void => {
                                onFilterChange({ ...filter, operator: newValue });
                            }}
                            fieldName={filter.field}
                        />
                    )}

                    {filter.field && (
                        <FilterValues
                            describeField={describeField}
                            filter={filter}
                            onChange={(newValue, params): void =>
                                onFilterChange({ ...filter, values: newValue, params })
                            }
                            fieldName={filter.field}
                        />
                    )}
                </div>

                <div className="buttons">
                    <Button icon={<ICONS.CloseIcon />} danger onClick={() => removeTempFilter(index)} />
                </div>
            </div>
        );
    },
);

export default FiltersListField;
